import React from "react";
import PropTypes from "prop-types";
import { TextField, Typography, Stack } from "@mui/material";
import useAdminLimits from "src/hooks/useAdminLimits";

/**
 * TextField with an optional character limit from config/adminKeyValueLimit.json.
 *
 * Behaviour:
 *  - `limitKey` names the entry in the limits map (e.g. "m_name").
 *  - Uses `maxLength`, so input is blocked past the limit rather than silently
 *    truncated — matches how the member app behaves.
 *  - Shows a "12 / 50" counter under the field. Set `showCount={false}` to hide it
 *    (useful for tiny fields where the number is noise).
 *  - Falls back to a plain TextField when the key is missing from the config, so
 *    a form is safe to migrate before its keys are defined.
 *
 * Until the limits load, the field renders uncapped rather than flashing a wrong
 * cap; the counter appears once `ready`.
 */
export default function LimitedTextField({
  limitKey,
  showCount = true,
  helperText,
  ...textFieldProps
}) {
  const { getLimit, ready } = useAdminLimits();
  const limit = limitKey ? getLimit(limitKey) : undefined;

  if (!limit) {
    // No limit configured for this field — behave exactly like TextField.
    return <TextField {...textFieldProps} />;
  }

  const current = textFieldProps.value ?? "";
  const length = typeof current === "string" || typeof current === "number" ? String(current).length : 0;
  const over = length >= limit;

  return (
    <Stack spacing={0.25}>
      <TextField
        {...textFieldProps}
        inputProps={{ ...textFieldProps.inputProps, maxLength: limit }}
      />
      {showCount && ready && (
        <Typography
          variant="caption"
          color={over ? "text.secondary" : "text.disabled"}
          sx={{ alignSelf: "flex-end", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}
        >
          {length} / {limit}
        </Typography>
      )}
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {helperText}
        </Typography>
      )}
    </Stack>
  );
}

LimitedTextField.propTypes = {
  limitKey: PropTypes.string,
  showCount: PropTypes.bool,
  helperText: PropTypes.node
};